/**
 * Utility functions for JWT token management
 */

interface DecodedToken {
  exp: number;
  iat: number;
  userId: string;
  email: string;
  subscription: string;
}

/**
 * Decode JWT token without verification (client-side only for expiration check)
 */
export const decodeToken = (token: string): DecodedToken | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired or will expire within the given buffer time
 */
export const isTokenExpired = (
  token: string,
  bufferMinutes: number = 5
): boolean => {
  const decoded = decodeToken(token);
  if (!decoded) return true;

  const now = Date.now() / 1000; // Convert to seconds
  const bufferSeconds = bufferMinutes * 60;

  return decoded.exp - bufferSeconds <= now;
};

/**
 * Get time until token expires in minutes
 */
export const getTimeUntilExpiry = (token: string): number => {
  const decoded = decodeToken(token);
  if (!decoded) return 0;

  const now = Date.now() / 1000;
  const timeLeft = decoded.exp - now;

  return Math.max(0, Math.floor(timeLeft / 60)); // Convert to minutes
};

/**
 * Check if token needs refresh (expires within buffer time)
 */
export const shouldRefreshToken = (
  token: string,
  bufferMinutes: number = 5
): boolean => {
  return isTokenExpired(token, bufferMinutes);
};
